// @ts-strict-ignore
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { SvgIconName } from '@shared/svg-icon/svg-icon-name';
import { ProjectHeadVO } from '../../../model/vo/project.vo';

export class ProjectGroupUtils {
    public static readonly ROOT_GROUP_NAME: string = 'projectNavigation.my_projects';
    public static readonly FOLDER: number = 0;
    public static readonly MY_ASSIGNED_PROJECTS: number = 1;
    public static readonly DEFAULT_IMPORT_PROJECT_GROUP: number = 2;
    public static readonly PROJECT: number = 3;

    public static readonly MAX_GROUP_NAME_LENGTH: number = 500;

    public static isRootGroup(projectGroup: ProjectGroupVO): boolean {
        return projectGroup == null ? false : projectGroup.groupName == ProjectGroupUtils.ROOT_GROUP_NAME;
    }

    public static isFolderGroup(projectGroup: ProjectGroupVO): boolean {
        return projectGroup == null ? false : this.isFolderGroupType(projectGroup.groupType);
    }

    public static isFolderGroupType(groupType: number): boolean {
        return groupType === ProjectGroupUtils.FOLDER;
    }

    public static isMyAssignedProjectsGroup(projectGroup: ProjectGroupVO): boolean {
        return projectGroup === null ? false : this.isMyAssignedProjectsGroupType(projectGroup.groupType);
    }

    public static isMyAssignedProjectsGroupType(groupType: number): boolean {
        return groupType === ProjectGroupUtils.MY_ASSIGNED_PROJECTS;
    }

    public static isLocalizedProjectGroup(projectGroup: ProjectGroupVO): boolean {
        if (projectGroup === null) {
          return false;
        }
        return !(this.isFolderGroup(projectGroup) || this.isDefaultImportProjectGroup(projectGroup));
    }

    public static isDefaultImportProjectGroup(projectGroup: ProjectGroupVO): boolean {
        return projectGroup === null ? false : this.isDefaultImportProjectGroupType(projectGroup.groupType);
    }

    public static isDefaultImportProjectGroupType(groupType: number): boolean {
        return groupType === ProjectGroupUtils.DEFAULT_IMPORT_PROJECT_GROUP;
    }

    public static isConfigurationGroup(projectGroup: ProjectGroupVO): boolean {
        if (!projectGroup) {
            return false;
        }
        return this.isConfigurationGroupType(projectGroup.groupType);
    }

    public static isConfigurationGroupType(groupType: number): boolean {
        return groupType === ProjectGroupUtils.PROJECT;
    }

    public static projectGroupSortFunction(a: ProjectGroupVO, b: ProjectGroupVO, currentUserId: number, ascending: boolean = true ): number {

        let typeA: number = a.groupType;
        let typeB: number = b.groupType;

        // case when project group has project folder type (display after)
        if (ProjectGroupUtils.isConfigurationGroupType(typeA)) {
            typeA = -1;
        }
        if (ProjectGroupUtils.isConfigurationGroupType(typeB)) {
            typeB = -1;
        }

        if (typeA !== typeB) {
            return (-1 * NumberUtils.numericCompare(typeA, typeB));
        }

        // treat groupType 1 (myAssignedProject) as a normal group for sorting if current user is not the owner
        if (ProjectGroupUtils.isMyAssignedProjectsGroupType(typeA) &&
          a.groupOwner.userId !== currentUserId) {
            typeA = 0;
        }
        if (ProjectGroupUtils.isMyAssignedProjectsGroupType(typeB) &&
          b.groupOwner.userId !== currentUserId) {
            typeB = 0;
        }

        if (typeA !== typeB) {
            return (-1 * NumberUtils.numericCompare(typeA, typeB));
        }

        const stringCompareResult: number = StringUtils.stringCompare(a.groupName, b.groupName);
        // in case of equal names compare by id to ensure consistent sorting
        if (stringCompareResult === 0) {
            return NumberUtils.numericCompare(a.id, b.id);
        }
        return ascending ? stringCompareResult : (-1 * stringCompareResult);
    }

    public static findProjectGroupInATree(projectGroups: ProjectGroupVO[], targetId: number): ProjectGroupVO {
        const pg = projectGroups.find((pg: ProjectGroupVO) => pg.id === targetId);
        if (pg) {
            return pg;
        }
        for (let i = 0; i < projectGroups.length; i++) {
            const found = this.findProjectGroupInATree(projectGroups[i].groups, targetId);
            if (found) {
                return found;
            }
        }
    }


    public static isNewProjectGroup(projectGroup: ProjectGroupVO): boolean {
        if (!projectGroup) {
            return false;
        }
        return NumberUtils.isNotNumber(projectGroup.id) || projectGroup.id <= 0;
    }

    public static isTargetProjectGroupChildOfProjectGroup(targetProjectGroup: ProjectGroupVO, projectGroup: ProjectGroupVO): boolean {
        let parentProjectGroup = targetProjectGroup.parentGroup;
        while (parentProjectGroup) {
            if (parentProjectGroup.id === projectGroup.id) {
                return true;
            }
            parentProjectGroup = parentProjectGroup.parentGroup;
        }
        return false;
    }

  public static getProjectGroupIcon(entry: ProjectGroupVO, user: UserVO): SvgIconName {
    if (entry.groupType === ProjectGroupUtils.PROJECT) {
      if ('CAE_TYPE' === entry.importType) {
        return 'project-import';
      }
      return 'document';
    }
    if (user?.id === entry.groupOwner.userId) {
      return 'folder';
    }
    return 'folder-user';
  }

  public static isNewConfigurationGroup(projectGroup: ProjectGroupVO): boolean {
    if (!projectGroup || !ProjectGroupUtils.isConfigurationGroup(projectGroup)) {
      return false;
    }
    const projectGroupId: number = projectGroup.id;
    return NumberUtils.isNotNumber(projectGroupId) || projectGroupId <= 0;
  }

  public static isParentOfCurrentProject(projectGroup: ProjectGroupVO, currentProject: ProjectHeadVO): boolean {
    for (let child of projectGroup.projects) {
      if (child.id === currentProject.id) {
        return true;
      }
    }
    for (let child of projectGroup.groups) {
      if (ProjectGroupUtils.isParentOfCurrentProject(child, currentProject)) {
        return true;
      }
    }
    return false;
  }
}
